import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Trophy,
  CalendarDays,
  ListOrdered,
  Crown,
  HelpCircle,
  Settings,
  Medal,
  Flame,
  Target,
  Clock,
  ChevronDown,
} from 'lucide-react';
import {
  useWcOverview,
  useWcMatches,
  useWcLeaderboard,
  type WcPhase,
  type WcMatch,
} from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { initials } from '@isoft-people/utils';
import { PageTransition, FadeIn } from '@/components/ui/motion';
import { MatchCard } from '@/components/mundial/MatchCard';
import { BonusPicker } from '@/components/mundial/BonusPicker';
import { HowPointsModal } from '@/components/mundial/HowPointsModal';
import { BreakdownModal } from '@/components/mundial/BreakdownModal';
import { InstallBanner } from '@/components/mundial/InstallBanner';
import {
  PHASE_LABEL,
  PHASE_ORDER,
  PHASE_SHORT,
  dayKey,
  dayLabel,
  useViewerTimezone,
} from '@/components/mundial/common';
import { cn } from '@/lib/cn';

type Tab = 'partidos' | 'posiciones' | 'pronosticos';

export function MundialPage() {
  const realRole = useAuthStore((s) => s.user?.role);
  const myId = useAuthStore((s) => s.user?.id);
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'partidos';
  const setTab = (t: Tab) => setParams(t === 'partidos' ? {} : { tab: t });

  // Deep-link desde notificaciones push: /mundial?tab=partidos&phase=GROUP&match=12
  const phaseParam = params.get('phase') as WcPhase | null;
  const focusNumber = Number(params.get('match')) || null;
  const [phase, setPhase] = useState<WcPhase>(
    phaseParam && PHASE_ORDER.includes(phaseParam) ? phaseParam : 'GROUP',
  );
  const [group, setGroup] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  const { data: overview } = useWcOverview();

  const TABS: { key: Tab; label: string; icon: any }[] = [
    { key: 'partidos', label: 'Partidos', icon: CalendarDays },
    { key: 'posiciones', label: 'Posiciones', icon: ListOrdered },
    { key: 'pronosticos', label: 'Mis pronósticos', icon: Crown },
  ];

  return (
    <PageTransition className="flex flex-col gap-6">
      {/* Hero */}
      <FadeIn>
        <div className="relative overflow-hidden rounded-2xl bg-bg-sidebar p-4 text-white shadow-lg sm:p-6">
          <div className="brand-gradient absolute inset-x-0 top-0 h-1" />
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
                <Trophy size={26} className="text-primary-light" />
              </span>
              <div>
                <h1 className="text-2xl font-extrabold">Quiniela Mundial 2026</h1>
                <p className="text-sm text-white/60">
                  Pronostica, compite con tus colegas y llévate la gloria
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowHelp(true)}
                className="flex h-9 items-center gap-1.5 rounded-md bg-white/10 px-3 text-sm font-semibold text-white transition hover:bg-white/20"
              >
                <HelpCircle size={15} /> ¿Cómo se gana?
              </button>
              {realRole === 'ADMIN' && (
                <Link
                  to="/mundial/admin"
                  className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark"
                >
                  <Settings size={15} /> Admin
                </Link>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat icon={Flame} label="Mis puntos" value={overview?.myPoints ?? 0} accent />
            <Stat
              icon={Medal}
              label="Mi posición"
              value={overview?.myRank ? `#${overview.myRank}` : '—'}
            />
            <Stat icon={Target} label="Exactos" value={overview?.myExact ?? 0} />
            <Stat
              icon={Trophy}
              label="Partidos jugados"
              value={`${overview?.finishedMatches ?? 0}/${overview?.totalMatches ?? 104}`}
            />
          </div>
        </div>
      </FadeIn>

      <InstallBanner />

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border border-border bg-white p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              'flex h-10 flex-1 items-center justify-center gap-2 rounded-md text-sm font-semibold transition',
              tab === t.key
                ? 'bg-primary text-white shadow-sm'
                : 'text-font-secondary hover:bg-bg',
            )}
          >
            <t.icon size={16} /> <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {tab === 'partidos' && (
        <MatchesTab
          phase={phase}
          setPhase={setPhase}
          group={group}
          setGroup={setGroup}
          focusNumber={focusNumber}
        />
      )}
      {tab === 'posiciones' && <LeaderboardTab myId={myId} />}
      {tab === 'pronosticos' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <BonusPicker />
          <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
            <h3 className="mb-2 text-base font-bold text-font-primary">¿Cómo funciona?</h3>
            <ul className="space-y-2 text-sm text-font-secondary">
              <li>• Pronostica el marcador de cada partido antes del pitazo inicial.</li>
              <li>• Gana puntos por acertar el marcador, el ganador y los goleadores.</li>
              <li>• Las rondas finales valen más (multiplicadores por fase).</li>
              <li>• Suma extra con tus pronósticos de campeón, subcampeón y Bota de Oro.</li>
            </ul>
            <button
              onClick={() => setShowHelp(true)}
              className="mt-4 flex h-9 items-center gap-1.5 rounded-md border border-primary/30 bg-primary-subtle px-3 text-sm font-semibold text-primary transition hover:bg-primary hover:text-white"
            >
              <HelpCircle size={15} /> Ver tabla de puntos completa
            </button>
          </div>
        </div>
      )}

      {showHelp && <HowPointsModal onClose={() => setShowHelp(false)} />}
    </PageTransition>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: any;
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <div className="rounded-xl bg-white/5 p-3">
      <div className="flex items-center gap-1.5 text-[11px] text-white/50">
        <Icon size={12} /> {label}
      </div>
      <p className={cn('mt-1 text-2xl font-extrabold', accent ? 'text-primary-light' : 'text-white')}>
        {value}
      </p>
    </div>
  );
}

function MatchesTab({
  phase,
  setPhase,
  group,
  setGroup,
  focusNumber,
}: {
  phase: WcPhase;
  setPhase: (p: WcPhase) => void;
  group: string;
  setGroup: (g: string) => void;
  focusNumber?: number | null;
}) {
  const { data, isLoading } = useWcMatches({
    phase,
    group: phase === 'GROUP' && group ? group : undefined,
  });
  const items = data?.items ?? [];
  const tz = useViewerTimezone();
  const todayKey = dayKey(new Date().toISOString(), tz);

  // Un partido está "por pronosticar" si aún se puede predecir y no tiene pronóstico.
  const isPending = (m: WcMatch) =>
    !m.myPrediction && !m.locked && m.status === 'SCHEDULED' && !!m.homeTeam && !!m.awayTeam;
  const pendingCount = items.filter(isPending).length;
  const predictedCount = items.filter((m) => m.myPrediction).length;

  const [predFilter, setPredFilter] = useState<'todos' | 'pendientes' | 'pronosticados'>('todos');
  const filtered = useMemo(() => {
    if (predFilter === 'pendientes') return items.filter(isPending);
    if (predFilter === 'pronosticados') return items.filter((m) => m.myPrediction);
    return items;
  }, [items, predFilter]);

  // Agrupado por día: HOY y futuro primero (ascendente), luego pasado (reciente primero).
  const byDay = useMemo(() => {
    const m: Record<string, typeof items> = {};
    for (const it of filtered) (m[dayKey(it.kickoff, tz)] ||= []).push(it);
    return Object.entries(m).sort(([a], [b]) => {
      const af = a >= todayKey;
      const bf = b >= todayKey;
      if (af && bf) return a.localeCompare(b);
      if (!af && !bf) return b.localeCompare(a);
      return af ? -1 : 1;
    });
  }, [filtered, tz, todayKey]);

  // Colapsado por día. Por defecto, los días YA pasados quedan colapsados.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [collapseInit, setCollapseInit] = useState(false);
  useEffect(() => {
    if (collapseInit || items.length === 0) return;
    const past = new Set<string>();
    for (const it of items) {
      const k = dayKey(it.kickoff, tz);
      if (k < todayKey) past.add(k);
    }
    setCollapsed(past);
    setCollapseInit(true);
  }, [items, tz, todayKey, collapseInit]);
  const toggleDay = (k: string) =>
    setCollapsed((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  // Resumen de un día (para mostrar cuando está colapsado).
  const daySummary = (matches: typeof items) => {
    let pts = 0;
    let scored = 0;
    let pend = 0;
    for (const m of matches) {
      if (m.myPrediction && m.status === 'FINISHED') {
        pts += m.myPrediction.points;
        scored++;
      }
      if (isPending(m)) pend++;
    }
    return { pts, scored, pend };
  };

  // Deep-link desde notificación: expandir su día, hacer scroll y resaltar.
  const [highlighted, setHighlighted] = useState<number | null>(null);
  useEffect(() => {
    if (!focusNumber || items.length === 0) return;
    const target = items.find((m) => m.number === focusNumber);
    if (target) {
      const k = dayKey(target.kickoff, tz);
      setCollapsed((prev) => {
        if (!prev.has(k)) return prev;
        const n = new Set(prev);
        n.delete(k);
        return n;
      });
    }
    const t = setTimeout(() => {
      const el = document.getElementById(`wc-match-${focusNumber}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlighted(focusNumber);
      }
    }, 80);
    const t2 = setTimeout(() => setHighlighted(null), 6000);
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [focusNumber, items.length, tz]);

  const FILTERS = [
    ['todos', 'Todos', items.length],
    ['pendientes', 'Por pronosticar', pendingCount],
    ['pronosticados', 'Pronosticados', predictedCount],
  ] as const;

  return (
    <div className="flex flex-col gap-4">
      {/* Filtros de fase */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-white p-1">
          {PHASE_ORDER.map((p) => (
            <button
              key={p}
              onClick={() => setPhase(p)}
              className={cn(
                'h-8 rounded-md px-3 text-xs font-semibold transition',
                phase === p ? 'bg-primary text-white shadow-sm' : 'text-font-secondary hover:bg-bg',
              )}
            >
              {PHASE_SHORT[p]}
            </button>
          ))}
        </div>
        {phase === 'GROUP' && (
          <select
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="">Todos los grupos</option>
            {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'].map((g) => (
              <option key={g} value={g}>
                Grupo {g}
              </option>
            ))}
          </select>
        )}
        <span className="ml-auto text-xs text-font-tertiary">{PHASE_LABEL[phase]}</span>
      </div>

      {/* Filtros por estado del pronóstico */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setPredFilter(key)}
            className={cn(
              'flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition',
              predFilter === key
                ? 'border-primary bg-primary text-white shadow-sm'
                : 'border-border bg-white text-font-secondary hover:border-primary/40',
            )}
          >
            {label}
            <span
              className={cn(
                'rounded-full px-1.5 text-[10px] tabular-nums',
                predFilter === key ? 'bg-white/25' : 'bg-bg text-font-tertiary',
              )}
            >
              {count}
            </span>
          </button>
        ))}
      </div>
      <p className="-mt-2 flex items-center gap-1 text-[11px] text-font-tertiary">
        <Clock size={11} /> Los horarios se muestran en tu hora local. Toca un día para colapsarlo.
      </p>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 rounded-xl border border-border brand-shimmer" />
          ))}
        </div>
      ) : byDay.length === 0 ? (
        <div className="rounded-xl border border-border bg-white py-12 text-center text-sm text-font-tertiary">
          {predFilter === 'pendientes'
            ? '¡Vas al día! No tienes partidos sin pronosticar aquí.'
            : predFilter === 'pronosticados'
              ? 'Aún no has pronosticado partidos aquí.'
              : 'No hay partidos para mostrar.'}
        </div>
      ) : (
        byDay.map(([day, matches]) => {
          const isCol = collapsed.has(day);
          const isToday = day === todayKey;
          const sum = daySummary(matches);
          return (
            <div key={day} className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => toggleDay(day)}
                className="sticky top-0 z-10 flex w-full items-center gap-2 bg-bg/85 py-1.5 text-left backdrop-blur"
              >
                <ChevronDown
                  size={15}
                  className={cn('flex-shrink-0 text-font-tertiary transition', isCol && '-rotate-90')}
                />
                <CalendarDays size={14} className="flex-shrink-0 text-primary" />
                <h3 className="truncate text-sm font-bold capitalize text-font-primary">
                  {dayLabel(matches[0].kickoff, tz)}
                </h3>
                {isToday && (
                  <span className="flex-shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                    Hoy
                  </span>
                )}
                <span className="flex-shrink-0 text-xs text-font-tertiary">({matches.length})</span>
                {/* Resumen visible cuando el día está colapsado */}
                {isCol && (
                  <span className="ml-auto flex flex-shrink-0 items-center gap-2 text-xs">
                    {sum.scored > 0 && (
                      <span className="flex items-center gap-1 rounded-full bg-accent-light px-2 py-0.5 font-bold text-accent">
                        <Trophy size={11} /> {sum.pts} pts
                      </span>
                    )}
                    {sum.pend > 0 && (
                      <span className="rounded-full bg-warning/10 px-2 py-0.5 font-semibold text-warning">
                        {sum.pend} sin pronosticar
                      </span>
                    )}
                  </span>
                )}
              </button>
              {!isCol && (
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                  {matches.map((m) => (
                    <div
                      key={m.id}
                      id={`wc-match-${m.number}`}
                      className={cn(
                        'rounded-xl transition-shadow duration-500',
                        highlighted === m.number &&
                          'ring-2 ring-primary ring-offset-2 ring-offset-bg shadow-lg',
                      )}
                    >
                      <MatchCard match={m} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

function LeaderboardTab({ myId }: { myId?: string }) {
  const { data, isLoading } = useWcLeaderboard();
  const rows = data?.items ?? [];
  const podium = rows.slice(0, 3);
  const [selected, setSelected] = useState<{ userId: string; rank: number } | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-14 rounded-xl border border-border brand-shimmer" />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-white py-12 text-center">
        <ListOrdered size={28} className="mx-auto text-font-tertiary" />
        <p className="mt-2 text-sm text-font-secondary">
          Aún no hay participantes con puntos. ¡Sé el primero en pronosticar!
        </p>
      </div>
    );
  }

  const medal = ['text-warning', 'text-font-tertiary', 'text-[#CD7F32]'];

  return (
    <div className="flex flex-col gap-4">
      {/* Podio */}
      {podium.length >= 3 && (
        <div className="grid grid-cols-3 gap-3">
          {[podium[1], podium[0], podium[2]].map((p, idx) => {
            const realRank = p.rank;
            const heights = ['h-24', 'h-28', 'h-20'];
            return (
              <button
                type="button"
                key={p.userId}
                onClick={() => setSelected({ userId: p.userId, rank: realRank })}
                className="flex flex-col items-center justify-end transition hover:opacity-80"
              >
                <Avatar name={p.name} photoUrl={p.photoUrl} size={idx === 1 ? 'lg' : 'md'} />
                <p className="mt-1 max-w-full truncate text-xs font-semibold text-font-primary">
                  {p.name.split(' ')[0]}
                </p>
                <div
                  className={cn(
                    'mt-1 flex w-full flex-col items-center justify-center rounded-t-lg bg-gradient-to-b from-primary-subtle to-white',
                    heights[idx],
                  )}
                >
                  <Trophy size={16} className={medal[realRank - 1]} />
                  <p className="text-lg font-extrabold text-primary">{p.totalPoints}</p>
                  <p className="text-[10px] text-font-tertiary">#{realRank}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Tabla */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        {rows.map((r) => (
          <button
            type="button"
            key={r.userId}
            onClick={() => setSelected({ userId: r.userId, rank: r.rank })}
            className={cn(
              'flex w-full items-center gap-3 border-b border-border-light px-4 py-2.5 text-left transition last:border-0 hover:bg-bg',
              r.userId === myId && 'bg-primary-subtle hover:bg-primary-subtle',
            )}
          >
            <span
              className={cn(
                'w-6 text-center text-sm font-bold',
                r.rank <= 3 ? medal[r.rank - 1] : 'text-font-tertiary',
              )}
            >
              {r.rank}
            </span>
            <Avatar name={r.name} photoUrl={r.photoUrl} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-font-primary">
                {r.name}
                {r.userId === myId && <span className="ml-1 text-xs text-primary">(tú)</span>}
              </p>
              <p className="text-[11px] text-font-tertiary">
                {r.predictions} pronósticos
                {r.exactCount > 0 ? ` · ${r.exactCount} exactos` : ''}
                {r.bonusPoints > 0 ? ` · ${r.bonusPoints} pts de torneo` : ''}
              </p>
            </div>
            <span className="flex-shrink-0 text-lg font-extrabold tabular-nums text-primary">
              {r.totalPoints}
            </span>
          </button>
        ))}
      </div>

      <p className="text-center text-[11px] text-font-tertiary">
        Toca a cualquier colaborador para ver el detalle de sus puntos.
      </p>

      {selected && (
        <BreakdownModal
          userId={selected.userId}
          rank={selected.rank}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function Avatar({
  name,
  photoUrl,
  size = 'md',
}: {
  name: string;
  photoUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dim = size === 'lg' ? 'h-14 w-14 text-base' : size === 'md' ? 'h-11 w-11 text-sm' : 'h-9 w-9 text-xs';
  if (photoUrl) {
    return <img src={photoUrl} alt={name} className={cn('rounded-full object-cover', dim)} />;
  }
  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-light font-semibold text-white',
        dim,
      )}
    >
      {initials(name)}
    </div>
  );
}
